import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { returnCancelButtonStyle, returnToastStyle } from '../eQueueUtils';

const INACTIVITY_MS = 15 * 60 * 1000;
const COUNTDOWN_MS = 5 * 60 * 1000;

const InactivityToastContent: React.FC<{
	initialSeconds: number;
	onCancel: () => void;
	onExpire: () => void;
}> = ({ initialSeconds, onCancel, onExpire }) => {
	const [remaining, setRemaining] = useState(initialSeconds);
	const onExpireRef = useRef(onExpire);
	onExpireRef.current = onExpire;

	useEffect(() => {
		const timer = setInterval(() => {
			setRemaining((prev) => {
				if (prev <= 1) {
					clearInterval(timer);
					setTimeout(() => {
						return onExpireRef.current();
					}, 0);
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
		return () => {
			return clearInterval(timer);
		};
	}, []);

	const pad = (n: number) => {
		return String(n).padStart(2, '0');
	};
	const m = Math.floor(remaining / 60);
	const s = remaining % 60;

	return (
		<div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
			<span>
				Автоматическое отключение очереди из-за отсутствия активности [
				{pad(m)}:{pad(s)}]
			</span>
			<button onClick={onCancel} style={returnCancelButtonStyle}>
				Отмена
			</button>
		</div>
	);
};

interface UseInactivityTimerParams {
	isActive: boolean;
	onExpire: () => void;
}

export const useInactivityTimer = ({
	isActive,
	onExpire,
}: UseInactivityTimerParams) => {
	const inactivityTimerRef = useRef<ReturnType<typeof setTimeout>>();
	const toastIdRef = useRef<string | number | undefined>(undefined);
	const onExpireRef = useRef(onExpire);
	onExpireRef.current = onExpire;

	useEffect(() => {
		if (!isActive) {
			clearTimeout(inactivityTimerRef.current);
			if (toastIdRef.current !== undefined) {
				toast.dismiss(toastIdRef.current);
				toastIdRef.current = undefined;
			}
			return;
		}

		const startInactivityTimer = () => {
			clearTimeout(inactivityTimerRef.current);
			inactivityTimerRef.current = setTimeout(() => {
				toastIdRef.current = toast.error(
					<InactivityToastContent
						initialSeconds={Math.floor(COUNTDOWN_MS / 1000)}
						onCancel={() => {
							toast.dismiss(toastIdRef.current);
							toastIdRef.current = undefined;
							startInactivityTimer();
						}}
						onExpire={() => {
							toast.dismiss(toastIdRef.current);
							toastIdRef.current = undefined;
							onExpireRef.current();
						}}
					/>,
					{
						position: 'top-center',
						autoClose: false,
						hideProgressBar: true,
						closeOnClick: false,
						closeButton: false,
						draggable: false,
						style: returnToastStyle,
					},
				);
			}, INACTIVITY_MS);
		};

		const handleActivity = () => {
			if (toastIdRef.current !== undefined) {
				return;
			}
			startInactivityTimer();
		};

		document.addEventListener('mousemove', handleActivity, {
			passive: true,
		});
		document.addEventListener('click', handleActivity);
		startInactivityTimer();

		return () => {
			document.removeEventListener('mousemove', handleActivity);
			document.removeEventListener('click', handleActivity);
			clearTimeout(inactivityTimerRef.current);
			if (toastIdRef.current !== undefined) {
				toast.dismiss(toastIdRef.current);
				toastIdRef.current = undefined;
			}
		};
	}, [isActive]);
};
